/**
 * Cuentas por mes: todas las cuentas del año en una sola tabla.
 *
 * Filas agrupadas tipo → rubro → cuenta (los rubros se abren con un clic);
 * columnas = meses. Dos lecturas: movimiento de cada mes o saldo acumulado al
 * cierre. La intensidad de cada celda es relativa al mayor mes de su propia
 * fila, para que salte a la vista el mes que se sale de lo normal. Los montos
 * van con el signo de la naturaleza de la cuenta: rojo = contra naturaleza.
 * Clic en una celda de cuenta → libro mayor de ese mes.
 */
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, FileSpreadsheet, Loader2 } from "lucide-react";

import {
  Button,
  Cargando,
  ErrorCarga,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { hasPermission } from "../../lib/auth";
import { pesos } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import { descargarMatriz, fetchMatriz, fetchRubros, type Matriz } from "./api";
import {
  AvisoProvisorio,
  BarraPeriodo,
  SinDatos,
  usePeriodoUrl,
} from "./componentes";

const TIPOS = [
  { valor: "activo", texto: "Activo" },
  { valor: "pasivo", texto: "Pasivo" },
  { valor: "patrimonio", texto: "Patrimonio" },
  { valor: "ingreso", texto: "Ingresos" },
  { valor: "gasto", texto: "Gastos" },
] as const;

type Modo = "movimiento" | "saldo";
type Fila = Matriz["filas"][number];

type Formato = "miles" | "pesos" | "resumido";

const FORMATOS: { valor: Formato; texto: string; nota: string }[] = [
  { valor: "miles", texto: "En miles", nota: "montos en miles de pesos" },
  { valor: "pesos", texto: "En pesos", nota: "montos en pesos" },
  { valor: "resumido", texto: "Resumido", nota: "M = millones, k = miles" },
];
const CLAVE_FORMATO = "innovasuite.contabilidad.formato";

function formatoGuardado(): Formato {
  try {
    const v = localStorage.getItem(CLAVE_FORMATO);
    return v === "pesos" || v === "resumido" ? v : "miles";
  } catch {
    return "miles";
  }
}

const ENTERO = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 });

/** Monto de una celda según el formato elegido; `·` para cero. El exacto va en el `title`. */
function formatear(v: number, formato: Formato): string {
  if (v === 0) return "·";
  if (formato === "miles") {
    const miles = Math.round(v / 1_000);
    return miles === 0 ? (v < 0 ? "-0" : "0") : ENTERO.format(miles);
  }
  if (formato === "pesos") return ENTERO.format(v);
  return corto(v);
}

/** `14,3M`, `850k`. */
function corto(v: number): string {
  if (v === 0) return "·";
  const a = Math.abs(v);
  const s = v < 0 ? "-" : "";
  if (a >= 1_000_000) return `${s}${(a / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (a >= 1_000) return `${s}${Math.round(a / 1_000)}k`;
  return `${s}${a}`;
}

function acumular(m: number[]): number[] {
  let s = 0;
  return m.map((v) => (s += v));
}

function sumar(listas: number[][], n: number): number[] {
  return Array.from({ length: n }, (_, i) => listas.reduce((s, l) => s + (l[i] ?? 0), 0));
}

function Celdas({
  valores,
  modo,
  formato,
  intensidad,
  enlace,
}: {
  valores: number[];
  modo: Modo;
  formato: Formato;
  intensidad: boolean;
  enlace?: ((mes: number) => string) | undefined;
}): JSX.Element {
  const serie = modo === "saldo" ? acumular(valores) : valores;
  const max = Math.max(1, ...serie.map(Math.abs));
  const final = modo === "saldo" ? (serie[serie.length - 1] ?? 0) : serie.reduce((a, b) => a + b, 0);
  return (
    <>
      {serie.map((v, i) => {
        const fondo =
          intensidad && v !== 0
            ? { background: `rgba(58, 111, 176, ${(0.05 + (0.33 * Math.abs(v)) / max).toFixed(2)})` }
            : undefined;
        const clase = `whitespace-nowrap px-2 py-1 text-right tabular-nums ${v < 0 ? "text-rose-700" : ""}`;
        return (
          <td key={i} className={clase} style={fondo} title={pesos(v)}>
            {enlace && v !== 0 ? (
              <Link to={enlace(i + 1)} className="rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                {formatear(v, formato)}
              </Link>
            ) : (
              formatear(v, formato)
            )}
          </td>
        );
      })}
      <td
        className={`whitespace-nowrap border-l border-neutral-200 px-2 py-1 text-right font-medium tabular-nums ${final < 0 ? "text-rose-700" : ""}`}
        title={pesos(final)}
      >
        {formatear(final, formato)}
      </td>
    </>
  );
}

export function CuentasPorMesPage(): JSX.Element {
  const { state } = useAuth();
  const toast = useToast();
  const url = usePeriodoUrl();
  const [modo, setModo] = useState<Modo>("movimiento");
  const [tipo, setTipo] = useState("");
  const [formato, setFormatoEstado] = useState<Formato>(formatoGuardado);
  const setFormato = (f: Formato): void => {
    setFormatoEstado(f);
    try {
      localStorage.setItem(CLAVE_FORMATO, f);
    } catch {
      // Sin almacenamiento (modo privado): la preferencia dura la visita.
    }
  };
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [bajando, setBajando] = useState(false);
  const q = useQuery({
    queryKey: ["contabilidad", "matriz", url.filtro.anio, url.filtro.hasta],
    queryFn: () => fetchMatriz(url.filtro),
    // Al cambiar el período se mantiene lo anterior en pantalla mientras carga.
    placeholderData: keepPreviousData,
  });
  const rubros = useQuery({ queryKey: ["contabilidad", "rubros"], queryFn: fetchRubros });
  const m = q.data;
  const n = m?.columnas.length ?? 0;
  const anio = m?.periodo.anio ?? undefined;

  const grupos = useMemo(() => {
    if (!m) return [];
    return TIPOS.filter((t) => !tipo || t.valor === tipo)
      .map((t) => {
        const filas = m.filas.filter((f) => f.tipo === t.valor);
        const porRubro = new Map<string, Fila[]>();
        for (const f of filas) {
          const clave = f.rubro ?? "sin_rubro";
          porRubro.set(clave, [...(porRubro.get(clave) ?? []), f]);
        }
        return { ...t, filas, porRubro: [...porRubro.entries()] };
      })
      .filter((g) => g.filas.length > 0);
  }, [m, tipo]);
  const sinClasificar = m?.filas.filter((f) => !f.tipo) ?? [];

  const tituloRubro = (codigo: string): string =>
    rubros.data?.find((r) => r.codigo === codigo)?.titulo ?? "Sin rubro";
  const alternar = (rubro: string): void => {
    const next = new Set(abiertos);
    if (next.has(rubro)) next.delete(rubro);
    else next.add(rubro);
    setAbiertos(next);
  };
  const todos = m ? [...new Set(m.filas.map((f) => f.rubro ?? "sin_rubro"))] : [];
  const mayor = (cuentaId: number) => (mes: number) =>
    `/contabilidad/mayor/${cuentaId}?anio=${anio ?? ""}&md=${modo === "saldo" ? 1 : mes}&mh=${mes}`;

  const bajar = async (): Promise<void> => {
    setBajando(true);
    try {
      await descargarMatriz(url.filtro, modo);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo descargar el archivo");
    } finally {
      setBajando(false);
    }
  };

  const ingresos = m ? sumar(m.filas.filter((f) => f.tipo === "ingreso").map((f) => f.meses), n) : [];
  const gastos = m ? sumar(m.filas.filter((f) => f.tipo === "gasto").map((f) => f.meses), n) : [];

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="space-y-3">
        <PageHeader
          title="Cuentas por mes"
          actions={
            m &&
            !m.periodo.vacio && (
              <Button variant="secondary" onClick={() => void bajar()} disabled={bajando}>
                {bajando ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
                )}
                Excel
              </Button>
            )
          }
        />
        <div className="flex flex-wrap items-end gap-3">
          <BarraPeriodo periodo={m?.periodo} url={url} conDesde={false} />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div role="group" aria-label="Qué mostrar" className="flex rounded-md ring-1 ring-neutral-300">
            {(
              [
                ["movimiento", "Movimiento del mes"],
                ["saldo", "Saldo acumulado"],
              ] as const
            ).map(([valor, texto]) => (
              <button
                key={valor}
                type="button"
                aria-pressed={modo === valor}
                onClick={() => setModo(valor)}
                className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                  modo === valor ? "bg-brand-100 text-brand-800" : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {texto}
              </button>
            ))}
          </div>
          <div className="w-36">
            <Select
              label="Montos"
              value={formato}
              onChange={(e) => setFormato(e.target.value as Formato)}
            >
              {FORMATOS.map((f) => (
                <option key={f.valor} value={f.valor}>
                  {f.texto}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-44">
            <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.texto}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="secondary"
            onClick={() => setAbiertos(abiertos.size ? new Set() : new Set(todos))}
          >
            {abiertos.size ? "Contraer todo" : "Expandir todo"}
          </Button>
        </div>
        <AvisoProvisorio periodo={m?.periodo} />
      </div>

      {q.isLoading && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {m && m.periodo.vacio && <SinDatos puedeCargar={hasPermission(state, "contabilidad.cargar")} />}

      {m && !m.periodo.vacio && (
        <>
          <TableWrap label="Cuentas por mes">
            <table className={`w-full text-sm ${formato === "pesos" ? "min-w-[1280px]" : "min-w-[900px]"}`}>
              <caption className="sr-only">
                Cuentas contables por mes ({modo === "saldo" ? "saldo acumulado" : "movimiento del mes"})
              </caption>
              <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
                <tr>
                  <th className="sticky left-0 z-10 min-w-[240px] bg-neutral-100 px-3 py-2 text-left font-medium">
                    Cuenta
                  </th>
                  {m.columnas.map((c) => (
                    <th key={c.mes} className="px-2 py-2 text-right font-medium" title={c.provisorio ? "Mes provisorio" : undefined}>
                      {c.etiqueta.split(" ")[0]}
                      {c.provisorio && <span className="text-amber-700">*</span>}
                    </th>
                  ))}
                  <th className="border-l border-neutral-200 px-2 py-2 text-right font-medium">
                    {modo === "saldo" ? "Saldo" : "Total"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {grupos.map((g) => (
                  <Fragment key={g.valor}>
                    <tr className="bg-neutral-50 font-medium">
                      <td className="sticky left-0 z-10 bg-neutral-50 px-3 py-1.5 text-neutral-900">{g.texto}</td>
                      <Celdas
                        valores={sumar(
                          g.filas.map((f) => (f.contra ? f.meses.map((v) => -v) : f.meses)),
                          n,
                        )}
                        modo={modo}
                        formato={formato}
                        intensidad={false}
                      />
                    </tr>
                    {g.porRubro.map(([rubro, filas]) => {
                      const abierto = abiertos.has(rubro);
                      return (
                        <Fragment key={rubro}>
                          <tr className="hover:bg-neutral-50">
                            <td className="sticky left-0 z-10 bg-white px-3 py-1">
                              <button
                                type="button"
                                aria-expanded={abierto}
                                onClick={() => alternar(rubro)}
                                className="flex items-center gap-1 rounded text-left text-neutral-900 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                              >
                                {abierto ? (
                                  <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                )}
                                {tituloRubro(rubro)}
                                <span className="text-neutral-500">({filas.length})</span>
                              </button>
                            </td>
                            <Celdas valores={sumar(filas.map((f) => f.meses), n)} modo={modo} formato={formato} intensidad />
                          </tr>
                          {abierto &&
                            filas.map((f) => (
                              <tr key={f.cuenta_id} className="hover:bg-neutral-50">
                                <td className="sticky left-0 z-10 max-w-[280px] truncate bg-white py-1 pl-8 pr-3 text-neutral-700" title={`${f.codigo} ${f.nombre}`}>
                                  {f.nombre}
                                </td>
                                <Celdas valores={f.meses} modo={modo} formato={formato} intensidad enlace={mayor(f.cuenta_id)} />
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                ))}
                {sinClasificar.length > 0 && !tipo && (
                  <>
                    <tr className="bg-amber-50 font-medium">
                      <td className="sticky left-0 z-10 bg-amber-50 px-3 py-1.5 text-amber-800">Sin clasificar</td>
                      <Celdas valores={sumar(sinClasificar.map((f) => f.meses), n)} modo={modo} formato={formato} intensidad={false} />
                    </tr>
                    {sinClasificar.map((f) => (
                      <tr key={f.cuenta_id}>
                        <td className="sticky left-0 z-10 truncate bg-white py-1 pl-8 pr-3 text-neutral-700" title={f.codigo}>
                          {f.nombre}
                        </td>
                        <Celdas valores={f.meses} modo={modo} formato={formato} intensidad enlace={mayor(f.cuenta_id)} />
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
              {!tipo && modo === "movimiento" && (
                <tfoot className="border-t-2 border-neutral-300 bg-neutral-100 font-medium">
                  <tr>
                    <td className="sticky left-0 z-10 bg-neutral-100 px-3 py-1.5">Resultado del mes</td>
                    <Celdas valores={ingresos.map((v, i) => v - (gastos[i] ?? 0))} modo={modo} formato={formato} intensidad={false} />
                  </tr>
                </tfoot>
              )}
            </table>
          </TableWrap>
          <p className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-600">
            <span>
              <span
                aria-hidden="true"
                className="mr-1 inline-block h-2.5 w-7 align-middle"
                style={{ background: "linear-gradient(90deg, rgba(58,111,176,0.05), rgba(58,111,176,0.38))" }}
              />
              Intensidad = monto comparado con el mayor mes de esa fila
            </span>
            <span className="text-rose-700">
              {modo === "saldo"
                ? "Rojo = saldo contra la naturaleza de la cuenta"
                : "Rojo = la cuenta bajó en el mes"}
            </span>
            <span>
              * = mes provisorio · {FORMATOS.find((f) => f.valor === formato)?.nota} · clic en un
              monto abre el libro mayor
            </span>
          </p>
        </>
      )}
    </div>
  );
}
